// src/components/forms/AnnouncementForm/hooks/useAnnouncementForm.js
import { useState, useEffect, useCallback } from "react";
import { t } from "i18next";
import { getUserInfo } from "../../../api/usercall"; // Assuming this exists
import { deleteUserLocationById } from "../../../api/post/deleteUserLocation"; // Assuming this exists
import { postAnnouncement } from "../../../api/createannouncement"; // This imports the postAnnouncement function from another file
import { getCategories } from "../../../api/getCategories"; // Import getCategories to get category names
import { API_URL } from "../../../config/connect"; // Import API_URL from config
import axios from 'axios'; // You've used axios here for the PUT request, ensure it's installed: npm install axios or yarn add axios

const useAnnouncementForm = (announcementData) => {
    // Determine initial price type - check both price_type and is_negotiable/negotiable fields
    const getInitialPriceType = (data) => {
        if (data?.price_type === "negotiable") {
            return "negotiable";
        }
        // Check if price is negotiable via is_negotiable or negotiable field
        if (data?.is_negotiable === true || data?.negotiable === true) {
            return "negotiable";
        }
        // If price is null or undefined, it might be negotiable
        if (data?.price === null || data?.price === undefined) {
            return "negotiable";
        }
        return data?.price_type || "fixed";
    };

    // State variables for form fields
    const [priceType, setPriceType] = useState(getInitialPriceType(announcementData));
    const [announcementName, setAnnouncementName] = useState(announcementData?.title || '');
    const [videoLink, setVideoLink] = useState(announcementData?.video_url || '');
    const [description, setDescription] = useState(announcementData?.description || '');
    const [selectedCategory, setSelectedCategory] = useState(announcementData?.category_id || null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(announcementData?.subcategory_id || null);
    // For new architecture, we always work with catalog-based location object on the client.
    // If backend already returns location with ids, we can prefill from it. Otherwise, user must re-select.
    const initialLocation = announcementData?.location && (
        announcementData.location.country_id ||
        announcementData.location.region_id ||
        announcementData.location.district_id
    )
        ? announcementData.location
        : null;
    const [selectedLocation, setSelectedLocation] = useState(initialLocation);
    // Clean price value - handle "None", null, undefined
    const getInitialPrice = (data) => {
        const priceValue = data?.price;
        if (!priceValue || priceValue === 'None' || priceValue === 'null' || priceValue === null || priceValue === undefined) {
            return '';
        }
        return priceValue.toString();
    };
    const [price, setPrice] = useState(getInitialPrice(announcementData));
    const [images, setImages] = useState(announcementData?.image_urls || []);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [userData, setUserData] = useState(null);
    const [UserLocations, setUserLocations] = useState(null);
    const [tagsArray, setTagsArray] = useState(announcementData?.tags || []);
    const [customTags, setCustomTags] = useState('');
    const [additionalPhones, setAdditionalPhones] = useState(announcementData?.additional_phones || []);
    const [phoneErrors, setPhoneErrors] = useState(additionalPhones.map(() => false));
    const [isLoading, setIsLoading] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // --- Unit Mappings ---
    // Map from server's full name to our internal key (for displaying/selecting)
    const serverUnitToKeyMap = {
        "kilogram": "kilogram",
        "gram": "gram",
        "liter": "liter",
        "pieces": "piece",
        "piece": "piece",
        "ton": "ton",
        "hectare": "hectare",
        "square_meter": "square_meter",
        "cubic_meter": "cubic_meter",
        "bag": "bag",
        "centner": "centner",
        "head": "head",
        "pair": "pair",
        "meter": "meter",
        "box": "box",
    };

    // Map from our internal key to server's full name (for sending to backend)
    // Server expects enum values: "piece", "kilogram", "ton", "liter", "hectare", "square_meter", "cubic_meter", "bag", "centner", "head", "pair", "meter", "box"
    const keyToServerUnitMap = {
        "kg": "kilogram",
        "g": "gram",
        "l": "liter",
        "pcs": "piece",
        "piece": "piece",
        "kilogram": "kilogram",
        "gram": "gram",
        "liter": "liter",
        "ton": "ton",
        "hectare": "hectare",
        "square_meter": "square_meter",
        "cubic_meter": "cubic_meter",
        "bag": "bag",
        "centner": "centner",
        "head": "head",
        "pair": "pair",
        "meter": "meter",
        "box": "box",
    };

    // Initialize volumeUnit - use server value directly if it matches, otherwise map it
    const getInitialVolumeUnit = () => {
        if (!announcementData?.measurement_unit) {
            return 'kilogram'; // Default
        }
        const serverUnit = announcementData.measurement_unit.toLowerCase();
        // If server unit is already in the correct format, use it directly
        const validUnits = ['piece', 'kilogram', 'ton', 'liter', 'hectare', 'square_meter', 'cubic_meter', 'bag', 'centner', 'head', 'pair', 'meter', 'box'];
        if (validUnits.includes(serverUnit)) {
            return serverUnit;
        }
        // Otherwise, try to map it
        return serverUnitToKeyMap[serverUnit] || 'kilogram';
    };

    const [volumeUnit, setVolumeUnit] = useState(getInitialVolumeUnit());


    // State for managing modal/menu visibility
    const [menuVisibility, setMenuVisibility] = useState({
        menu: false,
        user: false,
        address: false,
        search: false,
        language: false,
    });

    // Callback for toggling menu visibility
    const toggleMenu = useCallback(
        (menuName) => {
            setMenuVisibility((prev) => ({
                ...prev,
                [menuName]: !prev[menuName],
            }));
        },
        [setMenuVisibility]
    );

    // Callback for fetching user data on component mount
    const fetchUserData = useCallback(async () => {
        try {
            const response = await getUserInfo();
            setUserData(response.user);
            setUserLocations(response.locations);
        } catch (error) {
            console.error(t('failed_to_fetch_user_data'), error);
        }
    }, []);

    // Effect hook to fetch user data when the component mounts
    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    // Effect hook to update priceType and price when announcementData changes
    useEffect(() => {
        if (announcementData) {
            const newPriceType = getInitialPriceType(announcementData);
            setPriceType(newPriceType);
            const newPrice = getInitialPrice(announcementData);
            setPrice(newPrice);
        }
    }, [announcementData]);

    // Effect hook to set category names in localStorage when announcementData is loaded
    useEffect(() => {
        const setCategoryNamesInLocalStorage = async () => {
            if (announcementData?.category_id && announcementData?.subcategory_id) {
                try {
                    const categories = await getCategories();
                    const selectedCategory = categories.find(cat => cat.id === announcementData.category_id);
                    
                    if (selectedCategory) {
                        // Find the subcategory
                        const selectedSubcategory = selectedCategory.subcategories?.find(sub => sub.id === announcementData.subcategory_id);
                        
                        if (selectedSubcategory) {
                            // Set category and subcategory names in localStorage
                            localStorage.setItem("selectedCategoryName", selectedCategory.name);
                            localStorage.setItem("selectedSubcategoryName", selectedSubcategory.name);
                            localStorage.setItem("selectedCategoryId", selectedCategory.id);
                            localStorage.setItem("selectedSubcategoryId", selectedSubcategory.id);
                        }
                    }
                } catch (error) {
                    console.error("Error setting category names in localStorage:", error);
                }
            }
        };

        setCategoryNamesInLocalStorage();
    }, [announcementData]);

    // Handlers for updating form fields
    const handleAnnouncementInputChange = (value) => setAnnouncementName(value);
    const handleVideoLinkChange = (value) => setVideoLink(value);
    const handleDescriptionChange = (value) => setDescription(value);
    const handleSelectCategory = ({ categoryId, subcategoryId }) => {
        setSelectedCategory(categoryId.id);
        setSelectedSubcategory(subcategoryId.id);
    };
    const handleImagesChange = (imageFiles) => setImages(imageFiles);
    const handleSelectLocation = (location) => {
        setSelectedLocation(location);
    };
    const handleCustomTagsChange = (e) => {
        const value = e.target.value;
        setCustomTags(value);
    };
    // The handleVolumeChange now receives the 'unit key' (e.g., 'kg')
    const handleVolumeChange = (unitKey) => {
        setVolumeUnit(unitKey);
    };

    // Handlers for additional phone numbers
    const handleAddPhone = () => {
        setAdditionalPhones([...additionalPhones, '']);
        setPhoneErrors([...phoneErrors, false]);
    };

    const handlePhoneChange = (index, value) => {
        const updatedPhones = [...additionalPhones];
        updatedPhones[index] = value;
        setAdditionalPhones(updatedPhones);

        const updatedErrors = [...phoneErrors];
        updatedErrors[index] = false;
        setPhoneErrors(updatedErrors);
    };

    const handleDeletePhone = (index) => {
        const updatedPhones = additionalPhones.filter((_, i) => i !== index);
        setAdditionalPhones(updatedPhones);

        const updatedErrors = phoneErrors.filter((_, i) => i !== index);
        setPhoneErrors(updatedErrors);
    };

    // Handlers for tags
    const handleAddTag = () => {
        if (customTags.trim() === '') return;
        setTagsArray([...tagsArray, customTags.trim()]);
        setCustomTags('');
    };

    const handleRemoveTag = (index) => {
        setTagsArray(tagsArray.filter((_, i) => i !== index));
    };

    // Handler for deleting user addresses
    const handleDeleteAddress = async (addressId) => {
        try {
            const confirmation = window.confirm(t('confirm_delete_address'));
            if (!confirmation) return;

            await deleteUserLocationById(addressId);

            setUserLocations((prevLocations) =>
                prevLocations.filter((location) => location.address_id !== addressId)
            );

            if (selectedLocation === addressId) {
                setSelectedLocation(null);
            }

            console.log(t('address_deleted'));
        } catch (error) {
            console.error(t('error_deleting_address'), error);
            setErrorMessage(t('delete_address_error'));
            setShowErrorModal(true);
        }
    };

    // Form validation logic
    const validateForm = () => {
        const errors = [];

        if (!announcementName) errors.push(t('announcementNameError'));
        if (!description) errors.push(t('announcementDescriptionError'));
        if (!price && priceType === "fixed") errors.push(t('announcementPriceError'));
        if (!selectedLocation) {
            errors.push(t('announcementAddressError'));
            // Extra hint to move users from old user_location_id to new catalog-based location
            errors.push(t('announcementAddressMigrationHint'));
        }
        if (!tagsArray || tagsArray.length === 0) errors.push(t('tagsArrayError'));
        if (!selectedSubcategory) errors.push(t('announcementCategoryError'));

        const currentPhoneErrors = additionalPhones.map(phone => !phone.trim());
        if (currentPhoneErrors.some(error => error)) {
            errors.push(t('empty_phone_numbers_error'));
        }
        setPhoneErrors(currentPhoneErrors);

        if (errors.length > 0) {
            setErrorMessage(errors.map(error => `<p>${error}</p>`).join(""));
            setShowErrorModal(true);
            setIsSubmitted(true);
            return false;
        }
        return true;
    };

    // Builds FormData object for API submission
    const buildFormData = () => {
        const formData = new FormData();

        formData.append("title", announcementName || "");
        formData.append("video_url", videoLink || "");
        formData.append("description", description || "");
        formData.append("price_type", priceType || "");
        formData.append("price", priceType === "fixed" ? price : "");
        formData.append("isBarter", "false");

        // Convert to server format - server expects enum values
        // If volumeUnit is already in server format, use it directly
        // Otherwise, map it using keyToServerUnitMap
        let unitForServer = volumeUnit;
        if (keyToServerUnitMap[volumeUnit]) {
            unitForServer = keyToServerUnitMap[volumeUnit];
        } else {
            const validUnits = ['piece', 'kilogram', 'ton', 'liter', 'hectare', 'square_meter', 'cubic_meter', 'bag', 'centner', 'head', 'pair', 'meter', 'box'];
            if (!validUnits.includes(volumeUnit)) {
                // If it's not in the correct format, default to kilogram
                unitForServer = 'kilogram';
            }
        }
        formData.append("measurement_unit", unitForServer);

        if (selectedCategory) formData.append("category_id", selectedCategory);
        if (selectedSubcategory) formData.append("subcategory_id", selectedSubcategory);

        // New architecture for edit: send catalog-based location IDs; do not send user_location_id anymore.
        if (selectedLocation?.country_id != null) {
            formData.append("country_id", String(selectedLocation.country_id));
        }
        if (selectedLocation?.region_id != null) {
            formData.append("region_id", String(selectedLocation.region_id));
        }
        if (selectedLocation?.district_id != null) {
            formData.append("district_id", String(selectedLocation.district_id));
        }

        formData.append("contactName", userData?.name || "");
        formData.append("contactPhone", userData?.phone_number || "");

        additionalPhones.forEach((phone, index) => {
            formData.append(`additionalPhones[${index}]`, phone);
        });

        tagsArray.forEach(tag => {
            formData.append("tags[]", tag.trim());
        });

        images.forEach((file) => {
            if (file instanceof File) {
                formData.append("images", file);
            } else {
                formData.append("existing_image_urls[]", file);
            }
        });
        return formData;
    };

    // Handles publishing/submitting the announcement
    const handlePublish = async (announcementId) => {
        console.log(announcementId)
        if (!validateForm()) return { success: false };

        setIsSubmitted(true);
        setIsLoading(true);

        const formData = buildFormData();

        try {
            const accessToken = localStorage.getItem("access_token");
            let response;
            if (announcementId) {
                // If announcementId exists, it's an edit operation
                response = await axios.patch(`${API_URL}/api/v1/announcement/${announcementId}`, formData, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'multipart/form-data',
                        
                    },
                });
            } else {
                // Otherwise, it's a new announcement creation
                response = await postAnnouncement(formData, accessToken);
            }

            if (response?.announcement_id || response?.data?.announcement_id) {
                return { success: true, id: response.announcement_id || response.data.announcement_id };
            } else {
                setErrorMessage(t('announcement_id_missing'));
                setShowErrorModal(true);
                return { success: false };
            }
        } catch (error) {
            console.error("Error publishing announcement:", error);
            const errorMsg = error.response?.data?.message || error.message || t('error_creating_announcement');
            setErrorMessage(errorMsg);
            setShowErrorModal(true);
            return { success: false };
        } finally {
            setIsLoading(false);
        }
    };

    // Handles previewing the announcement data
    const handlePreview = () => {
        if (!validateForm()) return null; // Return null if validation fails

        setErrorMessage("");
        setShowErrorModal(false);

        const previewData = {
            title: announcementName,
            description,
            price,
            priceType,
            images,
            videoLink,
            userData,
            selectedLocation,
            additionalPhones,
            selectedSubcategory,
            selectedCategory,
            isBarter: false,
            tagsArray,
            volumeUnit, // Include volumeUnit (the key) in preview data
        };

        return previewData;
    };

    return {
        priceType, setPriceType,
        announcementName, handleAnnouncementInputChange,
        videoLink, handleVideoLinkChange,
        description, handleDescriptionChange,
        selectedCategory, selectedSubcategory, handleSelectCategory,
        selectedLocation, handleSelectLocation,
        price, setPrice,
        images, handleImagesChange,
        isSubmitted, setIsSubmitted,
        userData,
        UserLocations,
        tagsArray,
        customTags, handleCustomTagsChange, handleAddTag, handleRemoveTag,
        additionalPhones, handleAddPhone, handlePhoneChange, handleDeletePhone,
        isLoading,
        showErrorModal, setShowErrorModal,
        errorMessage, setErrorMessage,
        menuVisibility, toggleMenu,
        handlePublish,
        handlePreview,
        handleDeleteAddress,
        phoneErrors,
        volumeUnit, // Export volumeUnit (the key)
        handleVolumeChange, // Export handleVolumeChange
    };
};

export default useAnnouncementForm;